import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { SourcesPanel } from '@/features/sources/components/SourcesPanel';
import { printVisibility } from '@/features/styles/lib/printStyles';
import { AH_RAIL_WIDTH } from '@/theme';
import { Box, Flex, Icon, MotionBox, MotionFlex, Text } from '@/ui';
import type { IconName } from '@/ui/Icon/Icon';
import { CustomizeHome } from './CustomizeHome/CustomizeHome';
import { CUSTOMIZE_OPTIONS } from './customizeOptions';
import type { CustomizeControlId } from './customizeOptions';
import { RememberedEventListWindowsProvider } from './RememberedEventListWindowsProvider';
import { ScheduleSettingsPanel } from './ScheduleSettings/ScheduleSettingsPanel';
import { StylePanel } from './StylePanel/StylePanel';
import { TextMessagesPanel } from './TextMessages/TextMessagesPanel';
import { useCustomizeMode } from './useCustomizeMode';
import { ViewsPanel } from './ViewsPanel/ViewsPanel';

type PanelHeader = {
  icon: IconName;
  title: string;
  subtitlePrefix: string;
  highlight: string;
  subtitleSuffix: string;
};

// Used by the narrow rail (Style + Views). Full-screen panels carry their own header.
const PANEL_HEADERS: Partial<Record<CustomizeControlId, PanelHeader>> = {
  style: {
    highlight: 'every view',
    icon: 'Palette',
    subtitlePrefix: 'Color & vibe apply to ',
    subtitleSuffix: '.',
    title: 'Style · Whole Schedule',
  },
  views: {
    highlight: 'this view only',
    icon: 'Layout',
    subtitlePrefix: 'These settings apply to ',
    subtitleSuffix: '.',
    title: 'Customize Each View',
  },
};

const PANEL_BODIES: Record<CustomizeControlId, React.ReactNode> = {
  messages: <TextMessagesPanel />,
  schedule: <ScheduleSettingsPanel />,
  sources: <SourcesPanel />,
  style: <StylePanel />,
  views: <ViewsPanel />,
};

export const CustomizeSidebar = () => {
  const { activeControlId, addViewMode, isCustomizeModeActive } =
    useCustomizeMode();

  const { experience } = usePageExperienceContext();

  const isFullScreen =
    activeControlId !== null &&
    CUSTOMIZE_OPTIONS.find((o) => o.id === activeControlId)?.presentation ===
      'fullscreen';

  const isHome = isCustomizeModeActive && activeControlId === null;

  const header =
    activeControlId != null ? (PANEL_HEADERS[activeControlId] ?? null) : null;

  // Prevent the page from scrolling behind a fixed full-screen panel (Sources,
  // Text messages, Schedule settings). The rail — home included — doesn't cover it.
  useEffect(() => {
    if (!isFullScreen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isFullScreen]);

  return (
    <RememberedEventListWindowsProvider experienceId={experience.id}>
      <AnimatePresence>
        {/* Full-screen overlay for Schedule settings and Sources.
            position="fixed" paints over <PageExperience /> without affecting the flex layout.
            top="ah-bar" starts the overlay below the in-flow CustomizeBar (52px) so its
            content keeps the same top spacing as the Style/Views rail; zIndex={15} sits
            below the CustomizeBar (zIndex 20). */}
        {isFullScreen && activeControlId !== null && (
          <MotionBox
            animate={{ opacity: 1, y: 0 }}
            bg="surface.panel"
            bottom={0}
            exit={{ opacity: 0, y: 6 }}
            initial={{ opacity: 0, y: 12 }}
            key="fullscreen"
            left={0}
            overflow="hidden"
            position="fixed"
            right={0}
            sx={printVisibility.hide()}
            top="var(--chakra-sizes-ah-bar)"
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            zIndex={15}
          >
            {PANEL_BODIES[activeControlId]}
          </MotionBox>
        )}
        {/* Narrow rail for the home screen, Style, and Views.
            Outer MotionFlex animates width 0 → AH_RAIL_WIDTH, pushing content right.
            overflow="hidden" clips inner content so it reveals cleanly during expand.
            It sticks below the fixed CustomizeBar (--ah-sticky-top, set in
            EditPageExperience) rather than at 0, so the bar never covers the panel
            header — and its height fills exactly the viewport left under the bar.
            One shared key across home and both panels: picking a home card swaps the
            body in place instead of collapsing and re-expanding the rail. */}
        {!isFullScreen && (isHome || activeControlId !== null) && (
          <MotionFlex
            alignSelf="flex-start"
            animate={{ width: AH_RAIL_WIDTH }}
            exit={{ width: 0 }}
            flexShrink={0}
            h="calc(100vh - var(--ah-sticky-top, 0px))"
            initial={{ width: 0 }}
            key="rail"
            overflow="hidden"
            position="sticky"
            sx={printVisibility.hide()}
            top="var(--ah-sticky-top, 0px)"
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Fixed-width inner so the body is laid out once at the rail's
                resting width and simply revealed as the outer width animates,
                rather than reflowing through every intermediate width. Must not
                be a flex item that grows: `flex={1}` sets `flex-basis: 0%`,
                which overrides `w` and makes the body track the animation. */}
            <Flex
              bg="surface.panel"
              borderRight="1px solid"
              borderRightColor="line.soft"
              direction="column"
              flexShrink={0}
              minH={0}
              w={`${AH_RAIL_WIDTH}px`}
            >
              {/* Hide the panel header while the Add a View / Change Layout picker is
                  open — AddViewPicker (rendered inside ViewsPanel) owns that space. */}
              {header && addViewMode === null && (
                <Box borderBottom="1px solid" borderColor="line.panel" p={4}>
                  <Flex align="center" gap={1.5} mb={2}>
                    <Icon
                      color="magicPurple.600"
                      icon={header.icon}
                      size={3.5}
                    />
                    <Text
                      color="magicPurple.600"
                      fontSize="ah-label"
                      fontWeight={700}
                      letterSpacing="ah-label"
                      textTransform="uppercase"
                    >
                      {header.title}
                    </Text>
                  </Flex>
                  <Text color="ink.soft" fontSize="ah-control" lineHeight={1.4}>
                    {header.subtitlePrefix}
                    <Text as="span" color="magicPurple.600" fontWeight={700}>
                      {header.highlight}
                    </Text>
                    {header.subtitleSuffix}
                  </Text>
                </Box>
              )}
              {activeControlId !== null ? (
                PANEL_BODIES[activeControlId]
              ) : (
                <CustomizeHome />
              )}
            </Flex>
          </MotionFlex>
        )}
      </AnimatePresence>

      {/* The Add a View / Change Layout picker is now rendered inline inside
          ViewsPanel (the Views rail body) — no separate fixed overlay needed. */}
    </RememberedEventListWindowsProvider>
  );
};
